import { useMemo } from "react";
import { openSearchResultActionPanel } from "components/SearchResultActionPanel";
import { openGeoPointActionPanel } from "components/GeoPointActionPanel";
import { openPickerModal } from "components/PickerModal";
import {
    searchRouteWithItem,
    getRoute,
    folderRoute,
    docRoute,
    flatRoute,
    mailRoute,
} from "components/ItemUriLink";
import { check_isView } from "containers/ActionPanel/Utils";
import CommandActionHandler from "components/CommandActions";
import { usePostMessageHandler } from "./Handler";
import { useModalPostMessageHandler } from "./Modal";
import { DefaultAction } from "./Actions";
import { postMessageTo } from "./Utils";
import { toastStyled } from "data/toast";
import { PickerRenderType, PickerRenderTypeEnum } from "data/types";

type HistoryProps = {
    push: (url: string) => void;
};

type BrowseProps = {
    itemUri: string;
    mini?: boolean;
    externalLink?: boolean;
    isDoc?: boolean;
    toggles?: string | string[];
    view?: "doc" | "flat" | "folder" | "mail";
    mark?: string;
    print?: boolean;
    edit?: boolean;
};

type PickerModalProps = {
    itemUri: string;
    helpText?: string;
    pickerType?: string;
    selectButton?: string;
    toggles?: string | string[];
    scopeBreadcrumb?: boolean;
    additionalActions?: null | string;
    canChangeSite?: boolean;
    onSelect?: (eventKey: string) => string;
    defaultRenderType?: PickerRenderTypeEnum;
    allowedRenderTypes?: PickerRenderTypeEnum;
    filters?: any[];
    returnItem?: boolean;
};

const handleGeoInfo = (data: any) => {
    /* istanbul ignore else */
    if (data.row) {
        openSearchResultActionPanel({
            row: {
                ...data.row,
                targetView: "searchresult",
            },
        });
    } else if (check_isView({ views: ["upload"] })) {
        openGeoPointActionPanel({
            location: data,
        });
    }
};

const useDefaultPostMessageHandler = (history: HistoryProps) =>
    usePostMessageHandler(
        useMemo(
            () => ({
                [DefaultAction.Browse]: ({
                    itemUri,
                    mini,
                    externalLink,
                    isDoc,
                    toggles,
                    view,
                    mark,
                    print,
                    edit,
                }: BrowseProps) => {
                    let url;
                    switch (view) {
                        case "doc":
                            url = docRoute(
                                itemUri,
                                mini,
                                externalLink,
                                toggles,
                                mark,
                                print,
                                edit
                            );
                            break;
                        case "flat":
                            url = flatRoute(
                                itemUri,
                                mini,
                                externalLink,
                                toggles
                            );
                            break;
                        case "folder":
                            url = folderRoute(
                                itemUri,
                                mini,
                                externalLink,
                                toggles
                            );
                            break;
                        case "mail":
                            url = mailRoute(
                                itemUri,
                                mini,
                                externalLink,
                                toggles
                            );
                            break;
                        default:
                            url = getRoute({
                                itemUri,
                                isMini: mini,
                                isExternalLink: externalLink,
                                isDoc,
                                toggles,
                            });
                            break;
                    }
                    history.push(url);
                },
                [DefaultAction.Search]: (data: any) =>
                    history.push(searchRouteWithItem(/*searchItem*/ data)),
                [DefaultAction.GeoInfo]: handleGeoInfo,
                [DefaultAction.GeoInfoLegacy]: handleGeoInfo,
                [DefaultAction.Exec]: (data: {
                    actionId: string;
                    props: any;
                }) => CommandActionHandler(data.actionId, data.props),
                [DefaultAction.Toast]: toastStyled,
                [DefaultAction.Picker.Open]: async (
                    {
                        itemUri,
                        helpText,
                        pickerType,
                        selectButton,
                        toggles,
                        scopeBreadcrumb = false,
                        additionalActions = null,
                        canChangeSite = true,
                        defaultRenderType = PickerRenderType.Target,
                        allowedRenderTypes = PickerRenderType.Target,
                        filters = [],
                        returnItem = false,
                    }: PickerModalProps,
                    source: MessageEventSource | Window | null
                ) => {
                    let action: string | null = null;
                    const onSelect = (eventKey: string) => (action = eventKey);
                    const selectedItem = await openPickerModal({
                        itemUri,
                        pickerType,
                        helpText,
                        selectButton,
                        toggles,
                        scopeBreadcrumb,
                        additionalActions,
                        canChangeSite,
                        onSelect,
                        defaultRenderType,
                        allowedRenderTypes,
                        filters,
                        returnItem,
                    } as PickerModalProps);
                    if (selectedItem) {
                        postMessageTo(
                            DefaultAction.Picker.Select,
                            returnItem
                                ? { ...selectedItem, action }
                                : { itemUri: selectedItem, action },
                            source as Window
                        );
                    } else {
                        postMessageTo(
                            DefaultAction.Picker.Cancel,
                            null,
                            source as Window
                        );
                    }
                },
            }),
            [history]
        )
    );

export {
    usePostMessageHandler,
    useDefaultPostMessageHandler,
    useModalPostMessageHandler,
};
